import { useState, useEffect, useRef, useCallback } from 'react'
import ChatInterface from './components/ChatInterface'
import './App.css'

// Backend API URL - Fly.io deployment
// Force rebuild: 2025-10-27 16:47 - Fixed bot-mutl → bot-multi typo
const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://mejorhablemos-backend.fly.dev'

// Tenant configuration - can be set via URL param (?tenant=slug) or env var
function getTenantSlug(): string {
  // 1. Check URL param
  const urlParams = new URLSearchParams(window.location.search)
  const tenantFromUrl = urlParams.get('tenant')
  if (tenantFromUrl) {
    console.log('Using tenant from URL:', tenantFromUrl)
    return tenantFromUrl
  }

  // 2. Check env var
  const tenantFromEnv = (import.meta as any).env?.VITE_TENANT_SLUG
  if (tenantFromEnv) {
    console.log('Using tenant from env:', tenantFromEnv)
    return tenantFromEnv
  }

  // 3. Default
  console.log('No tenant specified, using default')
  return ''
}

const TENANT_SLUG = getTenantSlug()

/** n dígitos aleatorios (crypto si está disponible). */
function randomDigits(n: number): string {
  const values = new Uint32Array(n)
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(values)
  } else {
    for (let i = 0; i < n; i++) values[i] = Math.floor(Math.random() * 10)
  }
  return Array.from(values, (v) => String(v % 10)).join('')
}

export interface Message {
  id: string
  content: string
  sender: 'user' | 'bot'
  timestamp: Date
}

export interface UserData {
  fullName?: string
  email?: string
  phone?: string
  city?: string
  state?: string
  timezone?: string
}

function App() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: '¡Hola! Soy el asistente virtual de MejorHablemos. ¿En qué puedo ayudarte hoy?',
      sender: 'bot',
      timestamp: new Date(),
    },
  ])
  const [isLoading, setIsLoading] = useState(false)
  const [userData, setUserData] = useState<UserData | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(() => {
    // Restore sessionId from localStorage if available
    const stored = localStorage.getItem('mejorhablemos_chat_session')
    if (stored) {
      console.log('Restoring sessionId from localStorage:', stored)
    }
    return stored
  })
  const [phoneNumber] = useState(() => {
    // Persist phone number in localStorage to maintain session across page reloads
    const STORAGE_KEY = 'mejorhablemos_chat_phone'
    const stored = localStorage.getItem(STORAGE_KEY)

    if (stored) {
      console.log('Using existing phone number from localStorage:', stored)
      return stored
    }

    // Generate identifier for web chat (WEB-xxx format to distinguish from WhatsApp).
    // 10 dígitos ALEATORIOS (antes el timestamp: predecible y repetible entre
    // dos visitantes que abrían el widget en el mismo milisegundo).
    const newPhone = `WEB-${randomDigits(10)}`
    localStorage.setItem(STORAGE_KEY, newPhone)
    console.log('Generated new phone number:', newPhone)
    return newPhone
  })
  const pollingIntervalRef = useRef<number | null>(null)
  const messagesRef = useRef<Message[]>(messages)
  // Fase 1.4 — sessionToken (HMAC) que autentica el polling de mensajes de esta sesión.
  // En ref para no re-suscribir el polling cuando cambia.
  const sessionTokenRef = useRef<string | null>(
    localStorage.getItem('mejorhablemos_chat_session_token')
  )
  const saveSessionToken = (token?: string) => {
    if (token && token !== sessionTokenRef.current) {
      sessionTokenRef.current = token
      localStorage.setItem('mejorhablemos_chat_session_token', token)
    }
  }

  // Keep messagesRef in sync with messages state
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  const sendFile = async (file: File, message?: string) => {
    setIsLoading(true)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('phoneNumber', phoneNumber)
      if (message) {
        formData.append('message', message)
      }
      formData.append('userName', 'Usuario Web')

      const headers: Record<string, string> = {}
      if (TENANT_SLUG) {
        headers['X-Tenant-Slug'] = TENANT_SLUG
      }

      const response = await fetch(`${API_URL}/api/chat/upload`, {
        method: 'POST',
        headers, // No Content-Type - browser sets it for FormData
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Error al enviar archivo')
      }

      const data = await response.json()
      console.log('File upload response:', data)

      // Fase 1.4 — guardar el sessionToken (viene en cada respuesta del backend)
      saveSessionToken(data.metadata?.sessionToken)

      // Save sessionId if new
      if (data.metadata?.sessionId && !sessionId) {
        const newSessionId = data.metadata.sessionId
        setSessionId(newSessionId)
        localStorage.setItem('mejorhablemos_chat_session', newSessionId)
        console.log('Saved sessionId from file upload:', newSessionId)
      }

      // Update user data if available
      if (data.userData) {
        setUserData(data.userData)
      }

      console.log('File sent successfully, polling will update UI')
    } catch (error) {
      console.error('Error sending file:', error)
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: error instanceof Error ? error.message : 'Error al enviar el archivo. Por favor intenta nuevamente.',
        sender: 'bot',
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const sendMessage = async (content: string) => {
    // NO optimistic update - let polling handle it to avoid duplicates
    setIsLoading(true)

    try {
      // Send to backend
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      // Add tenant header if specified
      if (TENANT_SLUG) {
        headers['X-Tenant-Slug'] = TENANT_SLUG
      }

      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          phoneNumber,
          message: content,
          userName: 'Usuario Web Test',
        }),
      })

      if (!response.ok) {
        throw new Error('Error al enviar mensaje')
      }

      const data = await response.json()

      // Debug logging - remove after testing
      console.log('Backend response:', data)
      console.log('Response field:', data.response)
      console.log('Success field:', data.success)
      console.log('User data:', data.userData)
      console.log('Session ID:', data.metadata?.sessionId)

      // Fase 1.4 — guardar el sessionToken (viene en cada respuesta del backend)
      saveSessionToken(data.metadata?.sessionToken)

      // Save sessionId for polling (persist in localStorage)
      if (data.metadata?.sessionId && !sessionId) {
        const newSessionId = data.metadata.sessionId
        setSessionId(newSessionId)
        localStorage.setItem('mejorhablemos_chat_session', newSessionId)
        console.log('Saved sessionId to localStorage:', newSessionId)
      }

      // Update user data if available
      if (data.userData) {
        setUserData(data.userData)
      }

      // NO manual message adding - polling will fetch all messages including
      // the user message we just sent and the bot's response
      console.log('Message sent successfully, polling will update UI')
    } catch (error) {
      console.error('Error sending message:', error)
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: 'Lo siento, hubo un error de conexión. Por favor intenta nuevamente.',
        sender: 'bot',
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  // Polling function to fetch new messages from therapist
  const pollMessages = useCallback(async () => {
    if (!sessionId) return

    try {
      const pollHeaders: Record<string, string> = {}
      if (TENANT_SLUG) {
        pollHeaders['X-Tenant-Slug'] = TENANT_SLUG
      }
      // Fase 1.4 — autenticar el poll con el sessionToken (este widget ya usa headers).
      if (sessionTokenRef.current) {
        pollHeaders['X-Session-Token'] = sessionTokenRef.current
      }

      const response = await fetch(`${API_URL}/api/sessions/${sessionId}/messages`, {
        headers: pollHeaders,
      })

      if (!response.ok) {
        // Sesión inexistente (404), sessionToken inválido/corrupto (403) o ausente en modo
        // estricto (401, Fase 1.4): limpiar la sesión de localStorage y dejar de pollear
        // (evita reintentos infinitos). El próximo mensaje crea una sesión nueva con token.
        if (response.status === 404 || response.status === 403 || response.status === 401) {
          console.log(
            `Poll rechazado (${response.status}), limpiando sessionId/token de localStorage`
          )
          localStorage.removeItem('mejorhablemos_chat_session')
          localStorage.removeItem('mejorhablemos_chat_session_token')
          sessionTokenRef.current = null
          setSessionId(null)
        }
        console.error('Error fetching messages:', response.status)
        return
      }

      const data = await response.json()

      if (data.success && data.messages) {
        // DEBUG: Log raw messages from backend
        console.log('[Polling] Raw messages from backend:', data.messages.map((m: any) => ({
          id: m.id,
          sender: m.sender,
          direction: m.direction,
          contentPreview: m.content?.substring(0, 30),
        })))

        // Map backend messages to our Message format
        const backendMessages: Message[] = data.messages.map((msg: any) => ({
          id: msg.id,
          content: msg.content,
          sender: msg.direction === 'INBOUND' ? 'user' : 'bot',
          timestamp: new Date(msg.timestamp),
        }))

        // Use messagesRef.current instead of messages to avoid recreating this function
        const currentMessages = messagesRef.current

        // Only update if we have new messages from backend
        // Backend is the source of truth - always replace with backend messages
        const hasNewMessages =
          backendMessages.length !== currentMessages.length ||
          (backendMessages.length > 0 && currentMessages.length > 0 &&
           backendMessages[backendMessages.length - 1].id !== currentMessages[currentMessages.length - 1].id)

        if (hasNewMessages) {
          console.log('New messages detected, updating...', {
            backendCount: backendMessages.length,
            localCount: currentMessages.length,
            lastBackendId: backendMessages[backendMessages.length - 1]?.id,
            lastLocalId: currentMessages[currentMessages.length - 1]?.id,
          })
          // Replace all messages with backend version (source of truth)
          setMessages(backendMessages)
        }
      }
    } catch (error) {
      console.error('Error polling messages:', error)
    }
  }, [sessionId])

  // Setup polling when we have a sessionId
  useEffect(() => {
    if (sessionId) {
      console.log('Starting message polling for session:', sessionId)

      // Load messages immediately if sessionId was restored from localStorage
      pollMessages()

      // Poll every 3 seconds
      pollingIntervalRef.current = window.setInterval(pollMessages, 3000)

      // Cleanup on unmount
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
          console.log('Stopped message polling')
        }
      }
    }
  }, [sessionId, pollMessages])

  return (
    <div className="app">
      <ChatInterface
        messages={messages}
        onSendMessage={sendMessage}
        onSendFile={sendFile}
        isLoading={isLoading}
        userData={userData}
      />
    </div>
  )
}

export default App
